import {
  WorkflowConfigInputModel,
  WorkflowOrderFormInputModel,
  WorkflowOrderInputModel,
  WorkflowOrderStepStatus,
} from '@ail/orders-api'
import './WorkflowOrderFormEdit.scss'

export function WorkflowOrderFormEdit(props: {
  config: WorkflowConfigInputModel
  order: WorkflowOrderInputModel
  onChange: (value: WorkflowOrderInputModel) => void
}) {
  const { order, onChange } = props

  const updateForm = (patch: Partial<WorkflowOrderFormInputModel>) => {
    onChange({ ...order, form: { ...order.form, ...patch } })
  }

  const updateEnableWhenDone = (idx: number, value: string) => {
    const updated = [...order.form.enableWhenDone]
    updated[idx] = value
    updateForm({ enableWhenDone: updated })
  }

  const removeEnableWhenDone = (idx: number) =>
    updateForm({ enableWhenDone: order.form.enableWhenDone.filter((_, i) => i !== idx) })

  const updateEnableWhenKey = (oldKey: string, newKey: string) => {
    const updated: { [key: string]: string[] } = {}
    for (const [k, v] of Object.entries(order.form.enableWhen)) {
      updated[k === oldKey ? newKey : k] = v
    }
    updateForm({ enableWhen: updated })
  }

  const updateEnableWhenValue = (key: string, idx: number, value: string) => {
    const values = [...(order.form.enableWhen[key] ?? [])]
    values[idx] = value
    updateForm({ enableWhen: { ...order.form.enableWhen, [key]: values } })
  }

  const removeEnableWhenValue = (key: string, idx: number) =>
    updateForm({
      enableWhen: { ...order.form.enableWhen, [key]: order.form.enableWhen[key].filter((_, i) => i !== idx) },
    })

  const addEnableWhenValue = (key: string) =>
    updateForm({ enableWhen: { ...order.form.enableWhen, [key]: [...(order.form.enableWhen[key] ?? []), ''] } })

  const removeEnableWhenEntry = (key: string) => {
    const updated = { ...order.form.enableWhen }
    delete updated[key]
    updateForm({ enableWhen: updated })
  }

  return (
    <>
      <datalist id={`form-names-${props.order.form.name}`}>
        {props.config.orders.map((k) => (
          <option key={k.form.name} value={k.form.name} />
        ))}
      </datalist>
      <datalist id={`field-names-${props.order.form.name}`}>
        {props.order.form.fields.map((f) => (
          <option key={f.name} value={f.name} />
        ))}
      </datalist>
      <table className="workflow-order-form-edit" border={1}>
        <tbody>
          <tr>
            <th>Form Name</th>
            <td>
              <input value={order.form.name} onChange={(e) => updateForm({ name: e.target.value })} />
            </td>
          </tr>
          <tr>
            <th>Status</th>
            <td>
              <select
                value={order.form.status}
                onChange={(e) => updateForm({ status: e.target.value as WorkflowOrderStepStatus })}
              >
                {Object.values(WorkflowOrderStepStatus).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </td>
          </tr>
          <tr>
            <th>Active</th>
            <td>
              <input
                type="checkbox"
                checked={order.form.active}
                onChange={() => updateForm({ active: !order.form.active })}
              />
            </td>
          </tr>
          <tr>
            <th>Notify Tenant</th>
            <td>
              <input
                type="checkbox"
                checked={order.form.notifyTenant}
                onChange={() => updateForm({ notifyTenant: !order.form.notifyTenant })}
              />
            </td>
          </tr>
          <tr>
            <th>Enable When Done</th>
            <td>
              <table className="enable-when-done" border={1}>
                <tbody>
                  <tr>
                    <th>
                      <i
                        className="icon-add"
                        style={{ cursor: 'pointer', color: 'green' }}
                        onClick={() => updateForm({ enableWhenDone: [...order.form.enableWhenDone, ''] })}
                      />
                    </th>
                    {order.form.enableWhenDone.map((v, i) => (
                      <th style={{ cursor: 'pointer', color: 'red' }} key={i}>
                        <i className="icon-close" onClick={() => removeEnableWhenDone(i)} />
                      </th>
                    ))}
                  </tr>
                  {!!order.form.enableWhenDone.length && (
                    <tr>
                      <th></th>
                      {order.form.enableWhenDone.map((v, i) => (
                        <th key={i}>
                          <input
                            list={`form-names-${props.order.form.name}`}
                            value={v}
                            onChange={(e) => updateEnableWhenDone(i, e.target.value)}
                          />
                        </th>
                      ))}
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
          <tr>
            <th>Enable When</th>
            <td>
              <table className="enable-when" border={1}>
                <tbody>
                  <tr>
                    <th onClick={() => updateForm({ enableWhen: { ...order.form.enableWhen, '': [] } })}>
                      <i className="icon-add" style={{ cursor: 'pointer', color: 'green' }} />
                    </th>
                  </tr>
                  {Object.entries(order.form.enableWhen).map(([key, values]) => (
                    <tr key={key}>
                      <table border={1}>
                        <tr>
                          <th onClick={() => removeEnableWhenEntry(key)}>
                            <i className="icon-close" style={{ cursor: 'pointer', color: 'red' }} />
                          </th>
                          <th onClick={() => addEnableWhenValue(key)}>
                            <i className="icon-add" style={{ cursor: 'pointer', color: 'green' }} />
                          </th>
                          {values.map((v, i) => (
                            <th key={i} onClick={() => removeEnableWhenValue(key, i)}>
                              <i className="icon-close" style={{ cursor: 'pointer', color: 'red' }} />
                            </th>
                          ))}
                        </tr>
                        <tr>
                          <td>
                            <input
                              value={key}
                              onChange={(e) => updateEnableWhenKey(key, e.target.value)}
                              placeholder="field name"
                              style={{ width: 120 }}
                              list={`field-names-${props.order.form.name}`}
                            />
                          </td>
                          <td>
                            <i className="icon-right" />
                          </td>
                          {values.map((v, i) => (
                            <td key={i}>
                              <input
                                value={v}
                                onChange={(e) => updateEnableWhenValue(key, i, e.target.value)}
                                list={`form-names-${props.order.form.name}`}
                              />
                            </td>
                          ))}
                        </tr>
                      </table>
                    </tr>
                  ))}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    </>
  )
}
